import type { Layout } from "@motain/xpa-proto-files-web/lib/runtime/xpa";
import type { GetServerSideProps, InferGetServerSidePropsType } from "next";
import { useRouter } from "next/router";
import { fetchXpaLayout } from "@/utils/fetchXpaLayout";
import type { FC } from "react";
import { MatchScore } from "@/xpa-components/match-score/MatchScore";
import { MatchLineupWrapper } from "@/xpa-components/match-lineup/MatchLineupWrapper";
import { MatchInfo } from "@/xpa-components/match-info/MatchInfo";
import { MatchPrediction } from "@/xpa-components/match-prediction/MatchPrediction";
import { OttBannerComponent } from "@/xpa-components/ott-banner/OttBannerComponent";
import { PublisherImprintLink } from "@/xpa-components/publisher-imprint-link/PublisherImprintLink";
import { MatchEvents } from "@/xpa-components/match-events/MatchEvents";
import { Title } from "@/xpa-components/title/Title";
import { MatchStats } from "@/xpa-components/match-stats/MatchStats";
import { Standings } from "@/xpa-components/standings/Standings";
import { Gallery } from "@/xpa-components/gallery/Gallery";
import { RelatedNews } from "@/xpa-components/related-news/RelatedNews";
import { RelatedVideos } from "@/xpa-components/related-videos/RelatedVideos";
import { EmptyState } from "@/xpa-components/empty-state/EmptyState";
import { XpaGoogleAdsPlaceholder } from "@/modules/ads";
import { HorizontalSeparator } from "@/xpa-components/horizontal-separator/HorizontalSeparator";
import styles from "@/styles/pages/Match.module.scss";
import { UpcomingMatches } from "@/xpa-components/upcoming-matches/UpcomingMatches";
import { useMatchPage } from "@/components/match-page/useMatchPage";
import type { XpaSupportedComponentsResolverProps } from "@/components/xpa-layout/XpaLayout";
import { XpaLayout } from "@/components/xpa-layout/XpaLayout";
import {
  StreamName,
  useRegisterStream,
  useRegisterXpaStream,
} from "@/modules/analytics";
import { DefaultVideojsWrapper } from "@/xpa-components/videojs-wrapper/DefaultVideojsWrapper";
import { FormGuide } from "@/xpa-components/form-guide/FormGuide";
import { BettingWidget } from "@/xpa-components/betting-widget/BettingWidget";

const Match: FC<InferGetServerSidePropsType<typeof getServerSideProps>> = (
  props,
) => {
  const { asPath, locale = "en" } = useRouter();
  const { layout } = useMatchPage({
    initialLayout: props,
    path: `/${locale}${asPath}`,
  });

  useRegisterStream(StreamName.MatchStream);
  useRegisterXpaStream(props);

  return (
    <div className={styles.xpaContainer}>
      <XpaLayout
        layout={layout}
        XpaSupportedComponentsResolver={XpaSupportedComponentsResolverMatch}
        shouldMemoComponents
      />
    </div>
  );
};

export const getServerSideProps: GetServerSideProps<Layout> = async (ctx) => {
  return { props: await fetchXpaLayout(ctx) };
};

export default Match;

const XpaSupportedComponentsResolverMatch: XpaSupportedComponentsResolverProps =
  ({ contentType, uiKey }) => {
    switch (contentType.$case) {
      case "matchLineup":
        return <MatchLineupWrapper {...contentType.matchLineup} />;
      case "formGuide":
        return <FormGuide {...contentType.formGuide} />;
      case "matchScore":
        return <MatchScore {...contentType.matchScore} isPreloaded={true} />;

      case "matchInfo":
        return <MatchInfo {...contentType.matchInfo} />;

      case "matchPrediction":
        return <MatchPrediction {...contentType.matchPrediction} />;

      case "bettingWidget":
        return <BettingWidget {...contentType.bettingWidget} />;

      case "videojsPlayer":
        return (
          <DefaultVideojsWrapper
            videoJsOptions={contentType.videojsPlayer}
            id={uiKey}
          />
        );
      case "publisherImprintLink":
        return <PublisherImprintLink {...contentType.publisherImprintLink} />;

      case "matchEvents":
        return <MatchEvents {...contentType.matchEvents} />;

      case "title":
        return <Title {...contentType.title} />;

      case "matchStats":
        return <MatchStats {...contentType.matchStats} />;

      case "standings":
        return <Standings {...contentType.standings} />;

      case "gallery":
        return <Gallery {...contentType.gallery} />;

      case "relatedNews":
        return <RelatedNews {...contentType.relatedNews} />;

      case "relatedVideos":
        return <RelatedVideos {...contentType.relatedVideos} />;

      case "horizontalSeparator":
        return <HorizontalSeparator {...contentType.horizontalSeparator} />;

      case "emptyState":
        return <EmptyState {...contentType.emptyState} />;

      case "googleAdsPlaceholder":
        return (
          <XpaGoogleAdsPlaceholder {...contentType.googleAdsPlaceholder} />
        );

      case "ottBanner":
        return <OttBannerComponent {...contentType.ottBanner} />;

      case "upcomingMatches":
        return <UpcomingMatches {...contentType.upcomingMatches} />;
      default:
        return null;
    }
  };
